import { format } from "date-fns";
import { atom } from "jotai";
import { Node } from "prosemirror-model";

import {
	type DocumentMetadata,
	DocumentMetadataSchema,
	type DocumentType,
	type PageMetadata,
	generateShortUuid,
	parsePage,
	serializePage,
} from "@moment/api-collab";
import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";

import { $accessTokenFunc } from "../authenication";
import { $addDraftDocument, type DocsListItem } from "../document-list";
import { metadataKeyForDocsRoute } from "../document-metadata/documentMetadataKey";
import { $loadDocumentMetadataFromDisk } from "../document-metadata/load-metadata-from-disk.atom";
import { env } from "../env";
import { fs } from "../filesystem";
import { $git } from "../git";
import { buildDefaultPagePath } from "../page-metadata/page-metadata.atom";
import { paths } from "../paths";
import { isValidTemplateId } from "../templates/validation";
import { AGENTS_MD_TEMPLATE } from "./agents-template";
import { $setTemplateOnboarding } from "./template-onboarding.atom";
import {
	type TemplateOnboardingConfig,
	TemplateOnboardingConfigSchema,
} from "./template-onboarding.types";

const logger = new Logger("createDocument");

export type InitialPage = {
	/** Optional explicit page ID. If not provided, a random UUID is generated. */
	id?: string;
	title: string;
	slug: string;
	parentPageID: string | null;
	/** Optional explicit storage path relative to document root (e.g., "pages/2026/01/21").
	 *  If not provided, defaults to "pages/{pageId}". */
	storagePath?: string;
	/** Optional path to the page file relative to document root (e.g., "pages/2026/01/21/index.md").
	 *  Stored in page metadata so loading/saving uses the correct location. */
	path?: string;
};

export type CreateDocumentInput = {
	documentId: string;
	type?: DocumentType;
	title?: string;
	templateWorkspaceId?: string;
	templateId?: string;
	/** Optional initial pages to create. If not provided, a single "index" page is created. */
	initialPages?: InitialPage[];
};

export const $createDocumentWorkflow = atom(null, async (get, set, input: CreateDocumentInput) => {
	const documentId = input.documentId;

	if (!env.isDesktopApp) {
		throw new Error("Filesystem not available");
	}

	// STARTING CREATE DOCUMENT WORKFLOW

	logger.debug("Starting create document workflow", { documentId });

	// STEP 0: PREPARE DEPENDENCIES
	//
	// Get paths and filesystem atoms

	const pathsApi = paths();
	const fsApi = fs();
	const documentPath = await get(pathsApi.getDocumentDirPath(documentId));

	const isTemplate = input.templateWorkspaceId !== undefined && input.templateId !== undefined;
	const git = get($git);

	if (isTemplate) {
		// TEMPLATE CLONING WORKFLOW
		//
		// When creating from a template, we download the template as a tar.gz archive,
		// extract it, then update the metadata with a new document ID and timestamps.

		// Validate template IDs to prevent path traversal attacks (defense-in-depth)
		if (!isValidTemplateId(input.templateId!)) {
			throw new Error(`Invalid template ID: ${input.templateId}`);
		}
		if (!isValidTemplateId(input.templateWorkspaceId!)) {
			throw new Error(`Invalid template workspace ID: ${input.templateWorkspaceId}`);
		}

		logger.debug("Creating document from template", {
			documentId,
			templateWorkspaceId: input.templateWorkspaceId,
			templateId: input.templateId,
		});

		// Get access token for authenticated template download
		const { getAccessTokenSilently } = get($accessTokenFunc);
		const token = await getAccessTokenSilently?.();

		// Construct the archive URL for the template
		// Note that the url does take in a branch query parameter,
		// if none is passed, it will default to main.
		const remoteUrl = `${env.docsApiUrl}/internal/v2/repos/${input.templateWorkspaceId}/${input.templateId}/archive`;

		logger.debug("Downloading template archive", { remoteUrl, documentPath });

		// Ensure the documents directory exists before extracting the archive
		// The initFromArchive function will move the extracted archive to documentPath,
		// but the parent directory must exist first.
		const documentsDirPath = await get(pathsApi.getDocumentsDirPath());
		await set(fsApi.mkdir, { dirPath: documentsDirPath });

		// Download and extract the template archive, then initialize as a fresh git repo
		try {
			await git.initFromArchive({ path: documentPath, remoteUrl, token });
		} catch (error) {
			const msg = error instanceof Error ? error.message : String(error);
			logger.error("Failed to init document from template archive", {
				remoteUrl,
				documentPath,
				cause: msg,
			});
			throw new Error(`Failed to create document from template: ${msg}`);
		}

		logger.debug("Template archive extracted", { documentPath });

		// Read the cloned metadata from the template
		const metadataFilePath = await get(pathsApi.getDocumentMetadataFilePath(documentId));
		let clonedMetadata: DocumentMetadata;
		try {
			const parsed = await get(fsApi.readYamlFile({ filePath: metadataFilePath }));
			const result = DocumentMetadataSchema.safeParse(parsed);
			if (result.error) {
				throw result.error;
			}
			clonedMetadata = result.data;
		} catch (error) {
			logger.error("Failed to read metadata from cloned template", {
				metadataFilePath,
				cause: error,
			});
			throw new Error("Failed to read metadata from cloned template");
		}

		// Update metadata with new document ID and reset timestamps
		const documentMetadata: DocumentMetadata = {
			...clonedMetadata,
			id: documentId,
			type: undefined,
			created: new Date(),
			updated: new Date(),
		};

		// Write the updated metadata back to disk
		await set(fsApi.writeYamlFile, {
			filePath: metadataFilePath,
			content: documentMetadata,
			documentId,
		});

		logger.debug("Updated template metadata with new document ID", {
			documentId,
			metadataFilePath,
		});

		// Create initial commit for the new document
		const prettyTimestamp = format(new Date(), "PPppp");
		try {
			await git.commitAndRefresh({
				path: documentPath,
				message: `🚀 Created from template "${input.templateId}" at ${prettyTimestamp}`,
			});
		} catch (error) {
			logger.error("Failed to create initial commit for template document", {
				documentPath,
				cause: error,
			});
			throw new Error("Failed to create initial commit");
		}

		// Read .moment/template.yml for onboarding config (prompts, etc.)
		const templateYmlPath = `${documentPath}/.moment/template.yml`;
		try {
			const templateYmlExists = await get(fsApi.exists({ filePath: templateYmlPath }));
			if (templateYmlExists) {
				const parsed = await get(fsApi.readYamlFile({ filePath: templateYmlPath }));
				const result = TemplateOnboardingConfigSchema.safeParse(parsed);
				if (result.success && result.data.prompts.length > 0) {
					await set($setTemplateOnboarding, {
						documentId,
						config: result.data,
					});
					logger.debug("Template onboarding config saved", { documentId });
				}
			}
		} catch (error) {
			logger.warn("Failed to read .moment/template.yml, skipping onboarding", {
				templateYmlPath,
				cause: error,
			});
		}

		// Create DocsListItem for return value and optimistic update
		const docsListItem: DocsListItem = {
			id: documentId,
			local: {
				[documentPath]: {
					path: documentPath,
					title: documentMetadata.title,
					createdAt: documentMetadata.created,
					updatedAt: documentMetadata.updated,
					type: documentMetadata.type,
				},
			},
			remote: {},
		};

		// Add to draft documents so it appears immediately in the documents list
		set($addDraftDocument, docsListItem);

		// Load document metadata into atom system
		const metadataKey = metadataKeyForDocsRoute(documentId);
		set($loadDocumentMetadataFromDisk, { key: metadataKey, document: docsListItem });

		logger.info("Document created from template", {
			documentId,
			templateId: input.templateId,
			templateWorkspaceId: input.templateWorkspaceId,
		});

		return docsListItem;
	}

	logger.debug("Paths initialized!", { documentId, documentPath });

	// STEP 1: INITIALIZE GIT REPOSITORY
	//
	// Initialize git repository with the documentPath.
	// In the rust code, we will create the directories if they do not exist.
	// so it is safe and ideal to do this first.
	try {
		await git.init({ path: documentPath });
	} catch (error) {
		const msg = error instanceof Error ? error.message : String(error);

		// if it is already initialize, we can catch that,
		// but lets not do that and throw explicitly.
		logger.error("Git init failed", {
			documentPath,
			cause: msg,
		});
		throw error;
	}

	logger.debug("Git initialized!", { documentId, documentPath });

	// STEP 2: CREATE INITIAL PAGES
	//
	// We need to create the initial page(s) for the editor.
	// If initialPages is provided, create those. Otherwise create a single "index" page.

	const pagesToCreate: InitialPage[] = input.initialPages ?? [
		{ title: "", slug: "index", parentPageID: null },
	];

	// Map from temp parent IDs to actual page IDs (for hierarchical pages)
	const pageIdMap = new Map<string, string>();
	const pagesMetadata: PageMetadata[] = [];

	for (const pageInput of pagesToCreate) {
		// Use explicit ID if provided, otherwise generate a random one
		const pageId = pageInput.id ?? generateShortUuid();

		// Resolve parent ID if it references a previously created page
		const resolvedParentId = pageInput.parentPageID
			? (pageIdMap.get(pageInput.parentPageID) ?? pageInput.parentPageID)
			: null;

		const pageMetadata: PageMetadata = {
			id: pageId,
			title: pageInput.title,
			slug: pageInput.slug,
			archived: false,
			parentPageID: resolvedParentId,
			type: "markdown",
			...(pageInput.path && { path: pageInput.path }),
		};

		logger.debug("Creating page", { documentId, pageId, slug: pageInput.slug });

		// Determine the page file path
		let pageFilePath: string;
		if (pageInput.path) {
			// Use explicit path if provided (e.g., "daily/2026-01-22.md")
			pageFilePath = `${documentPath}/${pageInput.path}`;
			// Ensure the parent directory exists
			const parentDir = pageFilePath.substring(0, pageFilePath.lastIndexOf("/"));
			await set(fsApi.mkdir, { dirPath: parentDir });
			logger.debug("Page parent directory created", { parentDir });
		} else {
			// Default to the slug-derived path so the initial filesystem layout
			// matches later metadata-based loads/saves.
			let pageDirPath: string;
			if (pageInput.storagePath) {
				pageDirPath = `${documentPath}/${pageInput.storagePath}`;
				pageMetadata.path = `${pageInput.storagePath}/index.md`;
			} else {
				const derivedPath = buildDefaultPagePath(
					[...pagesMetadata, pageMetadata],
					pageMetadata
				);
				pageMetadata.path = derivedPath;
				const lastSlash = derivedPath.lastIndexOf("/");
				const dirRelative = lastSlash >= 0 ? derivedPath.slice(0, lastSlash) : derivedPath;
				pageDirPath = `${documentPath}/${dirRelative}`;
			}
			await set(fsApi.mkdir, { dirPath: pageDirPath });
			logger.debug("Page directory created", { pageDirPath });
			pageFilePath = pageMetadata.path
				? `${documentPath}/${pageMetadata.path}`
				: `${pageDirPath}/index.md`;
		}

		// Create page file
		const { doc: parsedDoc } = parsePage("");
		const initialDocContents = Node.fromJSON(schema, parsedDoc);
		const initialDocMdContents = serializePage(initialDocContents);
		await set(fsApi.writeTextFile, {
			filePath: pageFilePath,
			content: initialDocMdContents,
			documentId,
		});

		// Store the page ID mapping for child pages to reference
		// Use the slug as a temp ID since that's what we'll use to reference parents
		pageIdMap.set(pageInput.slug, pageId);

		pagesMetadata.push(pageMetadata);
		logger.debug("Page created!", { documentId, pageMetadata });
	}

	// STEP 3: CREATE DOCUMENT METADATA FILE.
	//
	// Write initial metadata file and load it into the atom system. This allows $createPage to
	// automatically update the metadata when it creates a page.
	const documentMetadata: DocumentMetadata = {
		id: input.documentId,
		title: input.title ?? "",
		...(input.type && { type: input.type }),
		created: new Date(),
		updated: new Date(),
		labels: [],
		pagesMetadata,
		schemaVersion: 1,
	};

	const metadataFilePath = await get(pathsApi.getDocumentMetadataFilePath(documentId));
	await set(fsApi.writeYamlFile, {
		filePath: metadataFilePath,
		content: documentMetadata,
		documentId,
	});

	logger.debug("Document metadata file created", { documentId, metadataFilePath });

	// STEP 4: CREATE AGENTS.md AND CLAUDE.md FILES
	//
	// Add AGENTS.md and CLAUDE.md with instructions for AI coding agents
	const agentsMdPath = `${documentPath}/AGENTS.md`;
	const claudeMdPath = `${documentPath}/CLAUDE.md`;
	await Promise.all([
		set(fsApi.writeTextFile, {
			filePath: agentsMdPath,
			content: AGENTS_MD_TEMPLATE,
			documentId,
		}),
		set(fsApi.writeTextFile, {
			filePath: claudeMdPath,
			content: AGENTS_MD_TEMPLATE,
			documentId,
		}),
	]);

	logger.debug("AGENTS.md and CLAUDE.md created", { documentId, agentsMdPath, claudeMdPath });

	// STEP 5: CREATE .moment/template.yml WITH DEFAULT PROMPTS
	//
	// Write a default template.yml so the AgentFAB shows suggestion chips.
	const defaultTemplateConfig: TemplateOnboardingConfig = { prompts: [] };
	const momentDirPath = `${documentPath}/.moment`;
	await set(fsApi.mkdir, { dirPath: momentDirPath });
	await set(fsApi.writeYamlFile, {
		filePath: `${momentDirPath}/template.yml`,
		content: defaultTemplateConfig,
		documentId,
	});
	await set($setTemplateOnboarding, { documentId, config: defaultTemplateConfig });

	logger.debug(".moment/template.yml created", { documentId });

	// Create DocsListItem for return value and optimistic update
	const docsListItem: DocsListItem = {
		id: documentId,
		local: {
			[documentPath]: {
				path: documentPath,
				title: documentMetadata.title,
				createdAt: documentMetadata.created,
				updatedAt: documentMetadata.created,
				type: input.type,
			},
		},
		remote: {},
	};

	// Add to draft documents so it appears immediately in the documents list. The
	// $documentsListEffect will merge this draft and persist it to cache.
	set($addDraftDocument, docsListItem);

	logger.debug("Added document to draft documents", { documentId, documentPath });

	// STEP 3: Create initial commit
	//
	// The page metadata has been automatically added to the atom system by $createPage. Now we
	// commit all changes to git.
	const prettyTimestamp = format(new Date(), "PPppp");
	try {
		await git.commitAndRefresh({
			path: documentPath,
			message: `🚀 Initial commit at ${prettyTimestamp}`,
		});
	} catch (error) {
		logger.error("Failed to create initial commit", { documentPath, cause: error });
		throw new Error("Failed to create initial commit");
	}

	logger.debug("git commit successful", { documentId, documentPath });

	// STEP 5: Load document metadata.
	//
	// The easy part.
	const metadataKey = metadataKeyForDocsRoute(documentId);
	set($loadDocumentMetadataFromDisk, { key: metadataKey, document: docsListItem });

	logger.debug("Document metadata loaded into atom system", { documentId, metadataKey });

	return docsListItem;
});
